<script setup>
import { computed } from 'vue';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import { useI18n } from 'vue-i18n';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import { useSettingsRowMenu } from '../components/useSettingsRowMenu';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['delete']);
// Phase 9.9: the page header (IntegrationHooks) owns the search now; the list reads it.
const searchQuery = defineModel('searchQuery', { type: String, default: '' });
const { t } = useI18n();
const { rowMenuItems, rowMenuLabel } = useSettingsRowMenu();

const { integration, isHookTypeInbox, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const hookHeaders = computed(() => {
  const headers = [...(integration.value.visible_properties || [])];
  if (isHookTypeInbox.value) {
    headers.push(t('INTEGRATION_APPS.LIST.INBOX'));
  }
  headers.push(t('INTEGRATION_APPS.LIST.ACTIONS'));
  return headers;
});

const hooks = computed(() => {
  if (!hasConnectedHooks.value) {
    return [];
  }
  const { hooks: integrationHooks } = integration.value;
  const visibleProperties = integration.value.visible_properties || [];
  return integrationHooks.map(hook => ({
    ...hook,
    id: hook.id,
    properties: visibleProperties.map(property =>
      hook.settings[property] ? hook.settings[property] : '--'
    ),
  }));
});

const filteredHooks = computed(() => {
  const query = searchQuery.value?.trim() || '';
  if (!query) return hooks.value;
  const lowerQuery = query.toLowerCase();
  return (
    hooks.value?.filter(hook =>
      hook.properties?.some(prop => prop?.toLowerCase().includes(lowerQuery))
    ) || []
  );
});

const inboxName = hook => (hook.inbox ? hook.inbox.name : '');
</script>

<template>
  <BaseTable
    v-if="hasConnectedHooks"
    :headers="hookHeaders"
    :items="filteredHooks"
    :no-data-message="searchQuery ? $t('INTEGRATION_APPS.NO_RESULTS') : ''"
    pin-last-column
    :clearable="!!searchQuery"
    @clear="searchQuery = ''"
  >
    <template #row="{ items }">
      <BaseTableRow v-for="hook in items" :key="hook.id" :item="hook">
        <BaseTableCell v-for="property in hook.properties" :key="property" wrap>
          {{ property }}
        </BaseTableCell>
        <BaseTableCell v-if="isHookTypeInbox">
          {{ inboxName(hook) }}
        </BaseTableCell>
        <BaseTableCell actions>
          <RowMenu
            :label="rowMenuLabel(hook.properties?.[0] || integration.name)"
            :items="rowMenuItems(['delete'])"
            @action="emit('delete', hook)"
          />
        </BaseTableCell>
      </BaseTableRow>
    </template>
  </BaseTable>
  <p
    v-else
    class="py-8 text-center type-polaris-body text-polaris-muted-foreground"
  >
    {{
      $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', {
        integrationId: integration.id,
      })
    }}
  </p>
</template>
